import { Component, IterableDiffer, IterableDiffers, KeyValueDiffer, KeyValueDiffers, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { InputStateDirective } from 'directives/input-state.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { TaskActionType } from 'enums/TaskActionType';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { TaskflowTemplateModel } from 'models/generated/Entities.GormCrm.TaskflowTemplate';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { ProductService } from 'services/product.service';
import { SalesService } from 'services/sales.service';
import { TaskService } from 'services/task.service';
import { TaskflowTemplateService } from 'services/taskflow-template.service';
import { UserService } from 'services/user.service';
import { AddTaskActionComponent } from './add-task-action.component';
import { NewTaskActionComponent } from './new-task-action.component';
import { Inputs } from 'modules/inputs';
import { Pipes } from 'modules/pipes';
import { ButtonComponent } from 'components/input/button/button.component';

@Component({
	selector: 'create-taskflow',
	templateUrl: './create-taskflow.component.html',
	styleUrls: ['./create-taskflow.component.scss'],
	providers: [FormValidationService],
	imports: [Layout, FormsModule, Inputs, ListView, AddTaskActionComponent, ReactiveFormsModule, InputStateDirective, NewTaskActionComponent, Pipes]
})
@ComponentUrl('create-taskflow')
@CredentialRequired(CredentialType.BetaUserAccess)
export class CreateTaskflowComponent {

	form: FormGroup = new FormGroup({ products: new FormGroup({}), title: new FormControl(), description: new FormControl() });
	productsGroup = new FormGroup({});
	taskflow: TaskflowModel = new TaskflowModel();
	taskflowTemplatesList: SelectListItemModel[] = [];
	usersList: SelectListItemModel[];
	products: OrderLineModel[] = [];
	inputTypeEnum = InputType;
	taskActionType = TaskActionType;

	selectedTaskAction?: TaskActionModel;

	actions: TaskActionModel[] = [];
	taskTypeList: SelectListItemModel[] = [];

	taskActionBusy = false;
	showTaskActionSelect = false;
	@ViewChild('selectTaskActionType') selectTaskActionType?: CustomSelectComponent;
	@ViewChild('saveButton') saveButton?: ButtonComponent;

	@UrlParam
	set companyId(id: string) {
		this.taskflow.companyId = id;
		this.salesService.getCompanyProducts(id).then(products => {
			products.sort((a, b) => {
				if (a.productVariant && b.productVariant)
					return this.productService.getVariantName(a.productVariant).localeCompare(this.productService.getVariantName(b.productVariant));
				return (a.created ? new Date(a.created).getTime() : new Date(0).getTime()) - (b.created ? new Date(b.created).getTime() : new Date(0).getTime());
			});
			this.products = products;
			let productControls: any = {};
			for (let product of products) {
				productControls[product.id as string] = new FormControl(false);
			}
			this.productsGroup = this.formBuilder.group(productControls);
		});

		this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent).then(viewCompany => { viewCompany.companyId = id; });
	}
	get companyId() {
		return this.taskflow.companyId as string;
	}

	differ: KeyValueDiffer<string, any>;
	defaultIterableDiffer: IterableDiffer<any>;
	taskFlowTemplates: TaskflowTemplateModel[] = [];
	constructor(private taskflowTemplateService: TaskflowTemplateService,
		private taskService: TaskService,
		userService: UserService,
		private salesService: SalesService,
		private navigation: NavigationService,
		private formBuilder: FormBuilder,
		private iterableDiffers: IterableDiffers,
		private differs: KeyValueDiffers,
		private readonly productService: ProductService) {
		this.taskflowTemplateService.getTemplates().then(templates => {
			this.taskFlowTemplates = templates;
			for (let template of templates) {
				this.taskflowTemplatesList.push(new SelectListItemModel(template.id as string, template.title));
			}
		});
		this.usersList = userService.getUserSelectList();

		this.defaultIterableDiffer = this.iterableDiffers.find(this.actions).create() as IterableDiffer<any>;
		this.differ = this.differs.find({}).create();
	}

	getSelectedProducts(): string[] {
		const formProducts = this.productsGroup.value;
		let selectedProductIds: string[] = [];
		for (let product in formProducts) {
			if (formProducts.hasOwnProperty(product) && formProducts[product as keyof typeof formProducts]) {
				selectedProductIds.push(product);
			}
		}
		return selectedProductIds;
	}

	deleteTaskAction(action: TaskActionModel) {
		this.actions.splice(this.actions.indexOf(action), 1);
		if (this.selectedTaskAction === action) {
			this.selectedTaskAction = undefined;
		}
	}

	async saveTaskflow() {
		this.taskflow.taskActions = this.actions;
		this.taskflow.include(t => t.taskActions);
		try {
			const taskflow = await this.taskService.createTaskflow(this.taskflow, this.getSelectedProducts());
			const view = await this.navigation.refreshView(ViewTaskflowComponent);
			if (taskflow.id) view.taskflowId = taskflow.id;
		}
		catch {
			this.saveButton?.reset();
		}
	}

	createNewTaskAction(taskAction: TaskActionModel) {
		this.actions.push(taskAction);
		this.selectedTaskAction = taskAction;
	}

	populateTaskActionList() {
		this.actions = [];
		let taskflowTemplate = this.taskFlowTemplates.find(tt => tt.id === this.taskflow.sourceTemplateId);

		if (taskflowTemplate) {
			taskflowTemplate.taskTemplates.forEach(taskTemplate => {
				let taskAction = new TaskActionModel();
				if (taskTemplate.defaultDeadline) {
					let date = new Date();
					date.setDate(date.getDate() + taskTemplate.defaultDeadline);
					taskAction.plannedDate = date;
					taskAction.sourceTemplateId = undefined;
				}
				taskAction.step = taskTemplate.step;
				taskAction.type = taskTemplate.type;
				taskAction.sourceTemplateId = taskTemplate.id;
				this.actions.push(taskAction);
			});
		}
	}
}
